// assets/collection-links.js
import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { cycleFocus } from '@theme/focus';
import { Scroller } from '@theme/scrolling';
import { center, clamp, closest, getVisibleElements } from '@theme/utilities';

/**
 * Collection links component
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} [images]
 * @property {HTMLElement[]} [links]
 * @property {import('slideshow').Slideshow} slideshow
 *
 * @extends {Component<Refs>}
 */
class CollectionLinks extends Component {
  requiredRefs = ['container'];

  /** @type {Scroller} */
  #scroll;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect
    );

    this.#scroll = new Scroller(this.refs.container, {
      onScroll: this.#handleScroll,
    });

    // hover / focus で画像切り替え
    this.addEventListener('mouseover', this.#handleMouseOver);
    this.addEventListener('focusin', this.#handleFocusIn);

    // マウスが全体から外れたら先頭に戻す
    this.addEventListener('mouseleave', this.#handleMouseLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('mouseover', this.#handleMouseOver);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);

    this.#scroll.destroy();
  }

  get links() {
    if (this.refs.links && this.refs.links.length) {
      return this.refs.links;
    }

    return Array.from(
      this.querySelectorAll(
        '[ref="links[]"], .collection-links__link, .collection-link, a'
      )
    ).filter((el) => this.refs.container?.contains(el));
  }

  get currentIndex() {
    return this.links.findIndex(
      (link) => link.getAttribute('aria-selected') === 'true'
    );
  }

  /**
   * Public method to select a collection link
   *
   * @param {number} targetIndex
   * @param {PointerEvent | MouseEvent} [event]
   */
  select(targetIndex, event) {
    this.#updateSelectedLink(targetIndex);
    this.refs.slideshow?.select(targetIndex, undefined, { animate: false });
    if (event) this.#revealImage(event);
  }

  /**
   * Update the selected link
   *
   * @param {number} index
   */
  #updateSelectedLink(index) {
    const { links } = this;
    const selectedIndex = clamp(index, 0, links.length - 1);

    for (const [index, link] of links.entries()) {
      link.setAttribute(
        'aria-selected',
        Boolean(index === selectedIndex).toString()
      );
    }
  }

  /**
   * Handle slideshow select event
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = async (event) => {
    if (!event.detail.userInitiated) return;

    const { index } = event.detail;
    if (index === this.currentIndex) return;

    const selectedLink = this.links[index];
    if (!selectedLink) return;

    this.#updateSelectedLink(index);

    this.#scroll.to(selectedLink);
  };

  /**
   * Handle hover
   *
   * @param {MouseEvent} event
   */
  #handleMouseOver = (event) => {
    const link = event.target.closest(
      '[ref="links[]"], .collection-links__link, .collection-link, a'
    );
    if (!link || !this.contains(link)) return;

    const index = this.links.indexOf(link);
    if (index < 0) return;

    this.select(index, event);
  };

  /**
   * Handle focus
   *
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    const link = event.target.closest(
      '[ref="links[]"], .collection-links__link, .collection-link, a'
    );
    if (!link || !this.contains(link)) return;

    const index = this.links.indexOf(link);
    if (index < 0) return;

    this.select(index);
  };

  /**
   * Handle mouse leave
   */
  #handleMouseLeave = () => {
    if (this.links.length > 0) {
      this.select(0);
    }
  };

  /**
   * Cycle focus to the next or previous link
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    let modifier = 0;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        modifier = 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        modifier = -1;
        break;
    }

    if (!modifier) return;

    event.preventDefault();
    cycleFocus(this.links, modifier);
  }

  /**
   * Handle scroll event
   */
  #handleScroll = () => {
    const { links } = this;
    const { container } = this.refs;
    const visibleLinks = getVisibleElements(this, links, 0.1);

    if (visibleLinks.length === 0) return;
    const centers = visibleLinks.map((link) => center(link, 'x'));
    const referencePoint = center(container, 'x');
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleLink = visibleLinks[centers.indexOf(closestCenter)];

    if (!closestVisibleLink) return;

    const index = links.indexOf(closestVisibleLink);

    this.select(index);
  };

  /**
   * Reveal an image
   *
   * @param {Event} event
   */
  #revealImage(event) {
    if (!(event instanceof MouseEvent || event instanceof PointerEvent)) return;

    const { target } = event;
    if (!(target instanceof HTMLElement)) return;

    const { images } = this.refs;
    const linkElement =
      target.closest('[ref="links[]"]') ||
      target.closest('.collection-links__link') ||
      target.closest('.collection-link') ||
      target.closest('a');

    if (!linkElement) return;

    const index = this.links.indexOf(linkElement);
    const selectedImage = images?.[index];

    if (!selectedImage) return;

    /** @param {MouseEvent | PointerEvent} moveEvent */
    const updateImagePosition = (moveEvent) => {
      const imageHeight = selectedImage.offsetHeight;
      const imageWidth = selectedImage.offsetWidth;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const offset = 15;

      const wouldBeCutOff =
        moveEvent.clientY + imageHeight + offset > viewportHeight;
      const yPos = wouldBeCutOff
        ? moveEvent.clientY - imageHeight - offset
        : moveEvent.clientY + offset;

      const xPos = Math.min(
        Math.max(offset, moveEvent.clientX + offset),
        viewportWidth - imageWidth - offset
      );

      selectedImage.style.setProperty('--x', `${xPos}px`);
      selectedImage.style.setProperty('--y', `${yPos}px`);
    };

    const reset = () => {
      selectedImage.removeAttribute('reveal');
      linkElement.removeEventListener('mousemove', updateImagePosition);
    };

    updateImagePosition(event);

    for (const image of images || []) {
      if (image === selectedImage) {
        image.setAttribute('reveal', '');
      } else {
        image.removeAttribute('reveal');
      }
    }

    linkElement.addEventListener('mousemove', updateImagePosition);
    linkElement.addEventListener('mouseleave', reset, { once: true });
  }
}

if (!customElements.get('collection-links-component')) {
  customElements.define('collection-links-component', CollectionLinks);
}
